// Reader for the Branch-maintained mortgage directories (issue #34139). Used by both
// backend lambdas (pointing at the core-resources endpoint with an X-Api-Key) and the
// frontends (pointing at their own same-origin proxy route, no key). Successful reads
// are cached in-instance for `cacheTtlMs`; on any failure the bundled snapshot is
// returned and NOT cached, so the next call retries the live source.
import { bundledMortgageLookups, bundledMortgageServiceContacts } from './data';
import type { MortgageLookup, MortgageServiceContact } from './types';

const DEFAULT_CACHE_TTL_MS = 10 * 60 * 1000; // 10 minutes

export interface MortgageDirectoryClientConfig {
  // Base URL the resource paths are appended to. BE: the core-resources endpoint.
  // FE: the app's proxy base (e.g. '/api/mortgage-directory').
  baseUrl: string;
  // X-Api-Key for direct core-resources access. Omit in the browser/proxy.
  apiKey?: string;
  // Injectable for tests and Node runtimes without a global fetch.
  fetchImpl?: typeof fetch;
  // Injectable clock for tests.
  now?: () => number;
  cacheTtlMs?: number;
  // Overridable so tests don't depend on the shipped snapshot.
  fallbackLookups?: MortgageLookup[];
  fallbackServiceContacts?: MortgageServiceContact[];
}

interface CacheEntry<T> {
  value: T;
  fetchedAt: number;
}

export class MortgageDirectoryClient {
  readonly #baseUrl: string;
  readonly #apiKey?: string;
  readonly #fetch: typeof fetch;
  readonly #now: () => number;
  readonly #cacheTtlMs: number;
  readonly #fallbackLookups: MortgageLookup[];
  readonly #fallbackServiceContacts: MortgageServiceContact[];

  #lookupsCache?: CacheEntry<MortgageLookup[]>;
  #contactsCache?: CacheEntry<MortgageServiceContact[]>;

  constructor(config: MortgageDirectoryClientConfig) {
    this.#baseUrl = config.baseUrl.replace(/\/+$/, '');
    this.#apiKey = config.apiKey;
    const fetchImpl = config.fetchImpl ?? (globalThis.fetch as typeof fetch | undefined);
    if (!fetchImpl) {
      throw new Error('MortgageDirectoryClient requires a fetch implementation');
    }
    this.#fetch = fetchImpl;
    this.#now = config.now ?? Date.now;
    this.#cacheTtlMs = config.cacheTtlMs ?? DEFAULT_CACHE_TTL_MS;
    this.#fallbackLookups = config.fallbackLookups ?? bundledMortgageLookups;
    this.#fallbackServiceContacts = config.fallbackServiceContacts ?? bundledMortgageServiceContacts;
  }

  async getMortgageLookups(): Promise<MortgageLookup[]> {
    if (this.#isFresh(this.#lookupsCache)) {
      return this.#lookupsCache!.value;
    }
    const fetched = await this.#read<MortgageLookup[]>('mortgage_lookups');
    if (fetched) {
      this.#lookupsCache = { value: fetched, fetchedAt: this.#now() };
      return fetched;
    }
    return this.#fallbackLookups;
  }

  async getMortgageServiceContacts(): Promise<MortgageServiceContact[]> {
    if (this.#isFresh(this.#contactsCache)) {
      return this.#contactsCache!.value;
    }
    const fetched = await this.#read<MortgageServiceContact[]>('mortgage_service_contacts');
    if (fetched) {
      this.#contactsCache = { value: fetched, fetchedAt: this.#now() };
      return fetched;
    }
    return this.#fallbackServiceContacts;
  }

  #isFresh(entry?: CacheEntry<unknown>): boolean {
    return !!entry && this.#now() - entry.fetchedAt < this.#cacheTtlMs;
  }

  async #read<T>(resource: string): Promise<T | null> {
    const headers: Record<string, string> = { Accept: 'application/json' };
    if (this.#apiKey) {
      headers['X-Api-Key'] = this.#apiKey;
    }
    try {
      const response = await this.#fetch(`${this.#baseUrl}/${resource}`, { headers });
      if (!response.ok) {
        return null;
      }
      return (await response.json()) as T;
    } catch {
      return null;
    }
  }
}
